import {useShowError} from "@hooks/useShowError/useShowError";
import {useMutation, useQuery} from "@tanstack/react-query";

import {useSnackBar} from "@hooks/alert/useSnackBar";
import {useTranslation} from "react-i18next";
import {
	addPointTemplateSingleVoyage,
	deletePointTemplateSingleVoyage,
	getPointTemplateByIdSingleVoyage,
	getPointTemplatesByIdsSingleVoyage,
	getPointTemplatesSingleVoyage,
	updatePointTemplateSingleVoyage
} from "@containers/singleVoyage/pointTemplates/api/api";
import {
	refetchPointTemplatesSingleVoyage
} from "@containers/singleVoyage/pointTemplates/refetch/refetchPointTemplatesSingleVoyage";
import {buildErrorsMessages} from "@ui/errors/helpers/helpers";
import {
	QUERY_KEY_POINT_TEMPLATE_BY_ID_SINGLE_VOYAGES,
	QUERY_KEY_POINT_TEMPLATES_BY_IDS_SINGLE_VOYAGES,
	QUERY_KEY_POINT_TEMPLATES_SINGLE_VOYAGES
} from "@containers/singleVoyage/pointTemplates/queryHooks/constants/queryKey";
import {refetchTrackableObjects} from "@containers/singleVoyage/trackableObjects/refetch/refetchTrackableObjects";
import {getCloseStopsErrorMessage} from "@containers/singleVoyage/containers/closeStops/closeStopsMessages";


export const useGetPointTemplatesSingleVoyageQuery = ({
														  accountId,
														  enabled,
													  }: {
	accountId: number
	enabled?: boolean
}) => {
	const {onShowError} = useShowError()
	
	return useQuery({
		queryFn: () => getPointTemplatesSingleVoyage({accountId}, onShowError),
		queryKey: [QUERY_KEY_POINT_TEMPLATES_SINGLE_VOYAGES, accountId?.toString() || ''],
		staleTime: 1000 * 60 * 30,
		enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!accountId,
	})
}

export const useGetPointTemplatesByIdsSingleVoyageQuery = ({
															   accountId,
															   ids,
															   enabled,
														   }: {
	accountId: number
	ids: number[]
	enabled?: boolean
}) => {
	const {onShowError} = useShowError()
	
	return useQuery({
		queryFn: () => getPointTemplatesByIdsSingleVoyage({accountId, ids}, onShowError),
		queryKey: [QUERY_KEY_POINT_TEMPLATES_BY_IDS_SINGLE_VOYAGES, accountId?.toString() || '', ids?.join('-')],
		staleTime: 1000 * 60 * 30,
		enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!accountId && ids?.length >= 1,
	})
}

export const useGetPointTemplateByIdSingleVoyageQuery = ({
															 accountId,
															 id,
															 enabled,
														 }: {
	accountId: number
	id: number
	enabled?: boolean
}) => {
	const {onShowError} = useShowError()
	
	return useQuery({
		queryFn: () => getPointTemplateByIdSingleVoyage({accountId, id}, onShowError),
		queryKey: [QUERY_KEY_POINT_TEMPLATE_BY_ID_SINGLE_VOYAGES, accountId?.toString() || '', id?.toString()],
		staleTime: 1000 * 60 * 30,
		enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!accountId && !!id,
	})
}

export const useAddPointTemplateSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()
	return useMutation({
		mutationFn: addPointTemplateSingleVoyage,
		onSuccess: () => {
			refetchPointTemplatesSingleVoyage()
			refetchTrackableObjects()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'info'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			const message = buildErrorsMessages(e)
			setSnackBar({
				message,
				variant: 'error'
			})
		}
	})
}

export const useUpdatePointTemplateSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()
	return useMutation({
		mutationFn: updatePointTemplateSingleVoyage,
		onSuccess: () => {
			refetchPointTemplatesSingleVoyage()
			refetchTrackableObjects()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'info'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			const message = getCloseStopsErrorMessage(e, 'stopTemplate') ?? buildErrorsMessages(e)
			setSnackBar({
				message,
				variant: 'error'
			})
		}
	})
}

export const useDeletePointTemplateSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	
	const {t} = useTranslation()
	
	return useMutation({
		mutationFn: deletePointTemplateSingleVoyage,
		onSuccess: () => {
			refetchPointTemplatesSingleVoyage()
			refetchTrackableObjects()
			setSnackBar({
				title: t('successDeleteMessage'),
				variant: 'info'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			const message = buildErrorsMessages(e)
			setSnackBar({
				message,
				variant: 'error'
			})
		}
	})
}